// Here i have not deleted the comments beacuse they need in future to add tabs for settings pane.

import { Box, styled, Tabs } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CoachPageLayout from '../../components/CoachPageLayout/CoachPageLayout';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/rootReducer';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { SingleTab } from '../Settings/Settings';
import {
  fetchCoachAvailabilities,
  fetchCoachConfigurations,
} from '../../redux/booking/bookingSlice';
import { UserPermissionType } from '../../types/commonTypes';
import UpdateAvailability from './UpdateAvailability';
import EventLimits from '../../components/CalenderSettings/EventLimits/EventLimits';
import { isRtl } from '../../utils/booking';
import i18n from '../../i18n';
import { CALENDAR_SETTINGS_ROUTE } from '../../utils/routes';
import { getCoachEventDetailsRequest } from '../../redux/coach/coachSlice';
import Packages from './Packages';
import LinkedAccounts from './LinkedAccounts';
import OfficeDetails from './OfficeDetails';
import { hideScrollbarStyles } from '../../utils/styles';

export const StyledBox = styled(Box)`
  background-color: #fff;
  border-radius: 10px;
  margin-top: 24px;
  min-width: 584px;
  box-shadow: 0px 4px 40px 0px #ffddc840;

  @media (max-width: 720px) {
    width: 100%;
  }
`;

const tabsProperties = [
  {
    label: 'bookings.bookingSettings.tabs.linkedAccounts',
    icon: '/assets/icons/view-bookings.svg',
    boldIcon: '/assets/icons/view-bookings-bold.svg',
    component: <LinkedAccounts />,
    tab: 'linked-accounts',
    permissions: [UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS],
  },
  {
    label: 'bookings.bookingSettings.tabs.hours',
    icon: '/assets/icons/clock.svg',
    boldIcon: '/assets/icons/clock-bold.svg',
    component: (
      <StyledBox width="max-content" maxHeight={596} gap="3px">
        <UpdateAvailability />
      </StyledBox>
    ),
    tab: 'hours',
    permissions: [UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS],
  },
  {
    label: 'bookings.bookingSettings.tabs.eventLimits',
    icon: '/assets/icons/event-limits.svg',
    boldIcon: '/assets/icons/event-limits-bold.svg',
    component: <EventLimits />,
    tab: 'event-limits',
    permissions: [UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS],
  },
  {
    label: 'bookings.bookingSettings.tabs.packages',
    icon: '/assets/icons/packages-icon.svg',
    boldIcon: '/assets/icons/packages-icon-bold.svg',
    component: <Packages />,
    tab: 'packages',
    permissions: [UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS],
  },
  {
    label: 'bookings.bookingSettings.tabs.generalSettings',
    icon: '/assets/icons/settings.svg',
    boldIcon: '/assets/icons/settings-bold.svg',
    component: <OfficeDetails />,
    tab: 'generalSettings',
    permissions: [UserPermissionType.BOOKINGS_MODULE_VIEW_COACH_CONFIGURATIONS],
  },
];

const IconWrapper = styled(Box)`
  width: 16px;
  height: 16px;
  display: flex;
  margin-top: 2px;
  justify-content: center;
  align-items: center;
`;

function tabPanelProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}
interface ITabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}
const CustomTabPanel = ({ children, value, index, ...other }: ITabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
    style={{
      height: '100%',
      width: '100%',
    }}
  >
    {value === index && <Box height="100%">{children}</Box>}
  </div>
);

function useURLQuery() {
  return new URLSearchParams(useLocation().search);
}

export interface ITabProperty {
  label: string;
  icon: string;
  boldIcon: string;
  component: React.ReactNode;
  permissions?: string[];
}

const BookingSettings = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const tab = useURLQuery().get('tab');
  const [validTabs, setValidTabs] = useState<string[]>([]);
  const [value, setValue] = useState(-2);
  const { t } = useTranslation();
  const [filteredTabProperties, setFilteredTabProperties] = useState<ITabProperty[]>([]);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  useEffect(() => {
    const filteredTabs = tabsProperties.filter((tabProperty) =>
      checkPermissions(tabProperty.permissions, false)
    );
    setFilteredTabProperties(filteredTabs);
    setValidTabs(filteredTabs.map((singleTab) => singleTab.tab));
  }, [tabsProperties]);

  useEffect(() => {
    const newValue = validTabs.indexOf(tab || validTabs[0]);
    setValue(newValue !== -1 ? newValue : 0);
  }, [value, id, navigate]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    const tabName = validTabs[newValue];
    navigate(`${CALENDAR_SETTINGS_ROUTE}?tab=${tabName}`);
  };

  const dispatch = useDispatch();
  const coach = useAppSelector((state: RootState) => state.coach.coach);

  useEffect(() => {
    if (!coach) return;
    dispatch(fetchCoachConfigurations({ coachId: coach?.id }));
    dispatch(fetchCoachAvailabilities({ coachId: coach?.id }));
    dispatch(getCoachEventDetailsRequest({ coachId: coach?.id }));
  }, [coach]);

  return (
    <CoachPageLayout>
      <Box width="100%" height="100%" pt={3}>
        <Box
          bgcolor="#fff !important"
          borderRadius="10px"
          border="0.5px solid #7CAEBE"
          justifyContent="center"
          boxShadow="0px 4px 40px 0px rgba(255, 221, 200, 0.25)"
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
            centered
            variant="fullWidth"
            sx={{
              width: '100%',
              height: '52px',
              borderRadius: '10px',
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            {filteredTabProperties.map(
              (
                singleTab: {
                  label: string;
                  icon: string;
                  boldIcon: string;
                  permissions?: string[];
                },
                index
              ) => (
                <SingleTab
                  key={singleTab.label}
                  label={<span>{t(singleTab.label)}</span>}
                  icon={
                    <IconWrapper>
                      <img
                        src={value === index ? singleTab.boldIcon : singleTab.icon}
                        alt={`${singleTab.label}-icon`}
                      />
                    </IconWrapper>
                  }
                  sx={{
                    borderLeft:
                      isRtl(i18n.language) && index < tabsProperties.length - 1
                        ? '0.5px solid #7CAEBE'
                        : 'none',
                    borderRight:
                      !isRtl(i18n.language) && index < tabsProperties.length - 1
                        ? '0.5px solid #7CAEBE'
                        : 'none',
                  }}
                  {...tabPanelProps(index)}
                />
              )
            )}
          </Tabs>
        </Box>
        <Box height="90%" sx={{ overflowY: 'auto', ...hideScrollbarStyles, maxHeight: '75vh' }}>
          {filteredTabProperties.map((tabName, index) => (
            <CustomTabPanel key={tabName.label} value={value} index={index}>
              {tabName.component}
            </CustomTabPanel>
          ))}
        </Box>
      </Box>
    </CoachPageLayout>
  );
};

export default BookingSettings;
